import React, { ChangeEvent, Dispatch, FC, SetStateAction, useCallback } from 'react';
import { Col, FormGroup, FormText, Input, Label } from 'reactstrap';
import { BaseCreateUser, sameAddress } from '../../shared/user/create';
import { useTranslation } from '../translations';
import { useCheckboxState, useInputModel, useObject, useUserSafe } from '../utils';
import { CountrySelector } from './CountrySelector';
import { DatePicker, getDefaultDate, PhoneInput, RegionSelector } from '.';

interface RegisterUserInfoProps {
  readonly forceValues?: boolean;
  readonly user: BaseCreateUser;
  readonly setUser: Dispatch<SetStateAction<BaseCreateUser>>;
}

export const RegisterUserInfo: FC<RegisterUserInfoProps> = ({ forceValues, user, setUser }) => {
  return (
    <>
      <RegisterUserInfoBasicDetails forceValues={forceValues} setUser={setUser} user={user} />
      <hr />
      <RegisterUserInfoAddress setUser={setUser} user={user} />
    </>
  );
};

export const RegisterSubUserInfo: FC<RegisterUserInfoProps> = ({ forceValues, user, setUser }) => {
  const { ts } = useTranslation();
  const currentUser = useUserSafe();
  const [differentAddress, setDifferentAddress] = useCheckboxState(!sameAddress(user, currentUser));

  const onSetDifferentAddress = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (!e.target.checked) {
        setUser((prev) => ({
          ...prev,
          addressLine1: currentUser.addressLine1,
          addressLine2: currentUser.addressLine2,
          addressCity: currentUser.addressCity,
          addressZipcode: currentUser.addressZipcode,
          addressCountry: currentUser.addressCountry,
          addressState: currentUser.addressState,
          phone: currentUser.phone,
        }));
      }

      setDifferentAddress(e);
    },
    [setUser, currentUser, setDifferentAddress],
  );

  return (
    <>
      <RegisterUserInfoBasicDetails forceValues={forceValues} setUser={setUser} user={user} />
      <hr />
      <FormGroup check className="mt-10 start-align">
        <Input
          checked={differentAddress}
          id="sameAddress"
          name="sameAddress"
          onChange={onSetDifferentAddress}
          type="checkbox"
        />
        <Label check for="sameAddress">
          {ts('registration.use_different_mailing_address')}
        </Label>
      </FormGroup>
      {differentAddress && <RegisterUserInfoAddress setUser={setUser} user={user} />}
    </>
  );
};

interface RegisterUserInfoBasicDetailsProps {
  readonly forceValues?: boolean;
  readonly user: BaseCreateUser;
  readonly setUser: Dispatch<SetStateAction<BaseCreateUser>>;
}

export const RegisterUserInfoBasicDetails: FC<RegisterUserInfoBasicDetailsProps> = ({
  forceValues,
  user,
  setUser,
}) => {
  const { ts } = useTranslation();
  const setFirstName = useInputModel(setUser, 'firstName');
  const setLastName = useInputModel(setUser, 'lastName');
  const setPreferredName = useInputModel(setUser, 'preferredName');
  const [bornAt, setBornAt] = useObject(user, setUser, 'bornAt');

  return (
    <>
      <FormGroup row>
        <Label for="firstName" sm={4}>
          {ts('common.first_name')}
        </Label>
        <Col sm={8}>
          <Input
            autoComplete="given-name"
            disabled={!!forceValues}
            id="firstName"
            name="firstName"
            onChange={setFirstName}
            placeholder={`${ts('common.example')}: John`}
            value={user.firstName ?? ''}
          />
          {forceValues ? (
            <FormText color="info">
              {ts('register_user_info.this_field_has_been_prepopulated_firstname')}
            </FormText>
          ) : (
            <FormText color="muted">{ts('common.this_must_be_your_legal_first')}</FormText>
          )}
        </Col>
      </FormGroup>
      <FormGroup row>
        <Label for="lastName" sm={4}>
          {ts('common.last_name')}
        </Label>
        <Col sm={8}>
          <Input
            autoComplete="family-name"
            disabled={!!forceValues}
            id="lastName"
            name="lastName"
            onChange={setLastName}
            placeholder={`${ts('common.example')}: Doe`}
            value={user.lastName ?? ''}
          />
          {forceValues ? (
            <FormText color="info">
              {ts('register_user_info.this_field_has_been_prepopulated_lastname')}
            </FormText>
          ) : (
            <FormText color="muted">{ts('common.this_must_be_your_legal')}</FormText>
          )}
        </Col>
      </FormGroup>
      <FormGroup row>
        <Label for="preferredName" sm={4}>
          {ts('common.preferred_name')}
          <small>({ts('common.optional')})</small>
        </Label>
        <Col sm={8}>
          <Input
            autoComplete="username"
            id="preferredName"
            name="preferredName"
            onChange={setPreferredName}
            placeholder={`${ts('common.example')}: Johnny`}
            value={user.preferredName || ''}
          />
          <FormText color="muted">{ts('common.if_you_have_a_name')}</FormText>
        </Col>
      </FormGroup>
      <FormGroup row>
        <Label for="bornAt" sm={4}>
          {ts('common.date_of_birth')}
        </Label>
        <Col sm={8}>
          <DatePicker
            date={bornAt || getDefaultDate()}
            disabled={!!forceValues}
            onChange={setBornAt}
          />
          {forceValues ? (
            <FormText className="mt-0" color="info">
              {ts('register_user_info.this_field_has_been_prepopulated_birthday')}
            </FormText>
          ) : (
            <FormText className="mt-0">{ts('register_user_info.you_must_be_at_least')}</FormText>
          )}
        </Col>
      </FormGroup>
    </>
  );
};

interface RegisterUserInfoAddressProps {
  readonly user: BaseCreateUser;
  readonly setUser: Dispatch<SetStateAction<BaseCreateUser>>;
}

export const RegisterUserInfoAddress: FC<RegisterUserInfoAddressProps> = ({ user, setUser }) => {
  const { ts } = useTranslation();
  const [country, setCountry] = useObject(user, setUser, 'addressCountry');
  const [region, setRegion] = useObject(user, setUser, 'addressState');
  const setAddressLine1 = useInputModel(setUser, 'addressLine1');
  const setAddressLine2 = useInputModel(setUser, 'addressLine2');
  const setAddressCity = useInputModel(setUser, 'addressCity');
  const setAddressZipcode = useInputModel(setUser, 'addressZipcode');
  const [phone, setPhone] = useObject(user, setUser, 'phone');

  return (
    <>
      <FormGroup row>
        <Label for="addressLine1" sm={4}>
          {ts('common.address_line_1')}
        </Label>
        <Col sm={8}>
          <Input
            autoComplete="address-line1"
            id="addressLine1"
            name="addressLine1"
            onChange={setAddressLine1}
            placeholder={`${ts('common.example')}: 123 Main St`}
            value={user.addressLine1 ?? ''}
          />
        </Col>
      </FormGroup>
      <FormGroup row>
        <Label for="addressLine2" sm={4}>
          {ts('common.address_line_2_optional')} <small>{ts('common.optional')}</small>
        </Label>
        <Col sm={8}>
          <Input
            autoComplete="address-line2"
            id="addressLine2"
            name="addressLine2"
            onChange={setAddressLine2}
            placeholder={`${ts('common.example')}: Apt 2`}
            value={user.addressLine2 ?? ''}
          />
        </Col>
      </FormGroup>
      <FormGroup row>
        <Label for="addressCity" sm={4}>
          {ts('common.city')}
        </Label>
        <Col sm={8}>
          <Input
            autoComplete="address-level2"
            id="addressCity"
            name="addressCity"
            onChange={setAddressCity}
            placeholder={`${ts('common.example')}: Reno`}
            value={user.addressCity ?? ''}
          />
        </Col>
      </FormGroup>
      <FormGroup row>
        <Label for="addressZipcode" sm={4}>
          {ts('common.zip_postal_code')}
        </Label>
        <Col sm={8}>
          <Input
            autoComplete="postal-code"
            id="addressZipcode"
            name="addressZipcode"
            onChange={setAddressZipcode}
            placeholder={`${ts('common.example')}: 89501`}
            value={user.addressZipcode ?? ''}
          />
        </Col>
      </FormGroup>
      <FormGroup row>
        <Label for="addressCountry" sm={4}>
          {ts('common.country')}
        </Label>
        <Col sm={8}>
          <CountrySelector
            autoComplete="country-name"
            className="form-control"
            id="addressCountry"
            name="addressCountry"
            onChange={setCountry}
            value={country || ''}
            valueType="short"
          />
        </Col>
      </FormGroup>
      <FormGroup row>
        <Label for="addressState" sm={4}>
          {ts('common.state_province')}
        </Label>
        <Col sm={8}>
          <RegionSelector
            className="form-control"
            country={country || ''}
            countryValueType="short"
            data-coerce={false}
            disableWhenEmpty
            id="addressState"
            name="addressState"
            onChange={setRegion}
            value={region || ''}
            valueType="short"
          />
        </Col>
      </FormGroup>
      <FormGroup row>
        <Label for="phone" sm={4}>
          {ts('common.phone_number')}
        </Label>
        <Col sm={8}>
          <PhoneInput
            defaultCountry={country ?? 'US'}
            name="phone"
            onChange={setPhone}
            value={phone ?? ''}
          />
        </Col>
      </FormGroup>
    </>
  );
};
